import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Search,
  Pencil,
  Trash2,
  RotateCcw,
  Loader2,
  ShoppingCart,
  Calendar,
  User,
  History,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Plus,
  Minus,
  X,
  FileText,
  Filter,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SalesManagement = ({ language = 'fr' }) => {
  const [sales, setSales] = useState([]);
  const [trashedSales, setTrashedSales] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'trash'
  
  // Filters
  const [searchTicket, setSearchTicket] = useState('');
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [editProducts, setEditProducts] = useState([]);
  const [editSupplements, setEditSupplements] = useState([]);
  const [editClientName, setEditClientName] = useState('');
  const [editTicket, setEditTicket] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Product search for adding new products
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [addingProductType, setAddingProductType] = useState('product'); // 'product' or 'supplement'
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchSales = React.useCallback(async (isRetry = false) => {
    setLoading(true);
    setLoadError(null);
    try {
      const token = localStorage.getItem('dermascan_token');
      
      if (!token) {
        console.warn('SalesManagement: No token in localStorage');
        // Don't show error immediately - check if user context has token
        setLoadError(null);
        setSales([]);
        setTrashedSales([]);
        setLoading(false);
        return;
      }
      
      if (viewMode === 'trash') {
        const response = await axios.get(`${API_URL}/sales/admin/trash`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000 // 15s timeout
        });
        setTrashedSales(response.data || []);
      } else {
        const params = new URLSearchParams();
        if (filterAgent && filterAgent !== 'all') params.append('agent_id', filterAgent);
        if (filterStartDate) params.append('start_date', filterStartDate);
        if (filterEndDate) params.append('end_date', filterEndDate);
        
        console.log('Fetching sales with params:', params.toString());
        const response = await axios.get(`${API_URL}/sales/admin/all?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000 // 15s timeout
        });
        console.log('Sales fetched:', response.data?.length || 0);
        setSales(response.data || []);
      }
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.error('Error fetching sales:', error);
      
      // Handle specific errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setLoadError(language === 'fr' ? 'Délai d\'attente dépassé. Réessayez.' : 'Request timeout. Please retry.');
      } else if (error.response?.status === 401) {
        setLoadError(language === 'fr' ? 'Session expirée. Veuillez vous reconnecter.' : 'Session expired. Please reconnect.');
        // Don't clear token here - let AuthContext handle logout
        toast.error(language === 'fr' ? 'Session expirée, reconnectez-vous' : 'Session expired');
      } else if (error.response?.status === 403) {
        setLoadError(language === 'fr' ? 'Accès refusé. Seuls les administrateurs peuvent accéder à cette page.' : 'Access denied. Admin only.');
      } else if (!navigator.onLine) {
        setLoadError(language === 'fr' ? 'Pas de connexion internet.' : 'No internet connection.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        console.error('Sales API error details:', errorMsg);
        setLoadError(language === 'fr' ? `Erreur: ${errorMsg}` : `Error: ${errorMsg}`);
      }
      
      // Auto-retry once for network errors
      if (!isRetry && retryCount < 1 && (error.code === 'ECONNABORTED' || !error.response)) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchSales(true), 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [viewMode, filterAgent, filterStartDate, filterEndDate, language, retryCount]);

  const fetchAgents = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const agentUsers = response.data.filter(u => ['agent', 'admin', 'manager'].includes(u.role));
      setAgents(agentUsers);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  }, []);

  useEffect(() => {
    fetchSales();
    fetchAgents();
  }, [fetchSales, fetchAgents]);

  // Search products in database
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const searchUrl = `${API_URL}/products/search?q=${encodeURIComponent(query)}&limit=20`;
      
      const response = await axios.get(searchUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      console.log('Search results for "' + query + '":', response.data?.length || 0);
      setProductSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProductSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchQuery) {
        searchProducts(productSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchQuery]);

  // Add product to the sale - auto-detect if supplement based on API response
  const addProductToSale = (product) => {
    const newProduct = {
      item_id: product.id,
      code: product.code,
      name: product.name,
      price: product.price || 0,
      quantity: 1,
      emplacement: product.emplacement || product.category || ''
    };
    
    // Auto-detect: if product has is_supplement flag from API, add to supplements
    const isSupplementProduct = product.is_supplement === true;
    
    if (isSupplementProduct) {
      // Check if already exists in supplements
      const exists = editSupplements.find(s => s.code === product.code || s.name === product.name);
      if (exists) {
        toast.info(language === 'fr' ? 'Ce complément est déjà dans la vente' : 'This supplement is already in the sale');
        return;
      }
      setEditSupplements(prev => [...prev, newProduct]);
      toast.success(language === 'fr' ? 'Complément ajouté' : 'Supplement added');
    } else {
      // Check if already exists in products
      const exists = editProducts.find(p => p.code === product.code || p.name === product.name);
      if (exists) {
        toast.info(language === 'fr' ? 'Ce produit est déjà dans la vente' : 'This product is already in the sale');
        return;
      }
      setEditProducts(prev => [...prev, newProduct]);
      toast.success(language === 'fr' ? 'Produit ajouté' : 'Product added');
    }
    
    setProductSearchQuery('');
    setProductSearchResults([]);
    setShowProductSearch(false);
  };

  const handleEdit = (sale) => {
    setSelectedSale(sale);
    setEditProducts([...(sale.products || [])]);
    setEditSupplements([...(sale.supplements || [])]);
    setEditClientName(sale.client_name || '');
    setEditTicket(sale.ticket_mediciel || '');
    setEditReason('');
    setShowProductSearch(false);
    setProductSearchQuery('');
    setProductSearchResults([]);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editReason.trim()) {
      toast.error(language === 'fr' ? 'Veuillez indiquer la raison de la modification' : 'Please provide a reason');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(`${API_URL}/sales/admin/${selectedSale.id}`, {
        products: editProducts,
        supplements: editSupplements,
        client_name: editClientName,
        ticket_mediciel: editTicket,
        reason: editReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'fr' ? 'Vente modifiée avec succès' : 'Sale updated successfully');
      setEditDialogOpen(false);
      fetchSales();
    } catch (error) {
      console.error('Error updating sale:', error);
      toast.error(error.response?.data?.detail || 'Error updating sale');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (sale) => {
    setSaleToDelete(sale);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(`${API_URL}/sales/admin/${saleToDelete.id}?reason=${encodeURIComponent(deleteReason)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'fr' ? 'Vente déplacée dans la corbeille' : 'Sale moved to trash');
      setDeleteDialogOpen(false);
      fetchSales();
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error(error.response?.data?.detail || 'Error deleting sale');
    }
  };

  const handleRestore = async (sale) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.post(`${API_URL}/sales/admin/${sale.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'fr' ? 'Vente restaurée' : 'Sale restored');
      fetchSales();
    } catch (error) {
      console.error('Error restoring sale:', error);
      toast.error(error.response?.data?.detail || 'Error restoring sale');
    }
  };

  const handlePermanentDelete = async (sale) => {
    if (!confirm(language === 'fr' 
      ? 'Supprimer définitivement cette vente ? Cette action est irréversible.'
      : 'Permanently delete this sale? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(`${API_URL}/sales/admin/${sale.id}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'fr' ? 'Vente supprimée définitivement' : 'Sale permanently deleted');
      fetchSales();
    } catch (error) {
      console.error('Error permanently deleting sale:', error);
      toast.error(error.response?.data?.detail || 'Error deleting sale');
    }
  };

  const showHistory = (sale) => {
    setHistoryData(sale.modification_history || []);
    setHistoryDialogOpen(true);
  };

  const updateProductQuantity = (index, delta) => {
    setEditProducts(prev => prev.map((p, i) => 
      i === index ? { ...p, quantity: Math.max(1, (p.quantity || 1) + delta) } : p
    ));
  };

  const removeProduct = (index) => {
    setEditProducts(prev => prev.filter((_, i) => i !== index));
  };

  const updateSupplementQuantity = (index, delta) => {
    setEditSupplements(prev => prev.map((s, i) => 
      i === index ? { ...s, quantity: Math.max(1, (s.quantity || 1) + delta) } : s
    ));
  };

  const removeSupplement = (index) => {
    setEditSupplements(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const productsTotal = editProducts.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0);
    const supplementsTotal = editSupplements.reduce((sum, s) => sum + (s.price || 0) * (s.quantity || 1), 0);
    return productsTotal + supplementsTotal;
  };

  const filteredSales = (viewMode === 'trash' ? trashedSales : sales).filter(sale => {
    if (searchTicket && !sale.ticket_mediciel?.toLowerCase().includes(searchTicket.toLowerCase())) {
      return false;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {language === 'fr' ? 'Gestion des Ventes' : 'Sales Management'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* View Mode Toggle */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'active' ? 'default' : 'outline'}
                onClick={() => setViewMode('active')}
                size="sm"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Ventes actives' : 'Active Sales'}
                <Badge className="ml-2 bg-green-100 text-green-800">{sales.length}</Badge>
              </Button>
              <Button
                variant={viewMode === 'trash' ? 'default' : 'outline'}
                onClick={() => setViewMode('trash')}
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Corbeille' : 'Trash'}
                <Badge className="ml-2 bg-red-100 text-red-800">{trashedSales.length}</Badge>
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>{language === 'fr' ? 'Rechercher ticket' : 'Search ticket'}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="N° ticket..."
                  value={searchTicket}
                  onChange={(e) => setSearchTicket(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="min-w-[180px]">
              <Label>{language === 'fr' ? 'Agent' : 'Agent'}</Label>
              <Select value={filterAgent} onValueChange={setFilterAgent}>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'fr' ? 'Tous les agents' : 'All agents'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les agents' : 'All agents'}</SelectItem>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="min-w-[150px]">
              <Label>{language === 'fr' ? 'Date début' : 'Start date'}</Label>
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </div>
            
            <div className="min-w-[150px]">
              <Label>{language === 'fr' ? 'Date fin' : 'End date'}</Label>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </div>
            
            <Button variant="outline" onClick={fetchSales}>
              <Filter className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Filtrer' : 'Filter'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sales Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
              <span className="ml-3 text-gray-500">
                {language === 'fr' ? 'Chargement des ventes...' : 'Loading sales...'}
              </span>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
              <p className="text-red-600 font-medium mb-2">{loadError}</p>
              <Button 
                onClick={() => fetchSales()} 
                variant="outline"
                className="mt-4"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Réessayer' : 'Retry'}
              </Button>
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {viewMode === 'trash' 
                ? (language === 'fr' ? 'La corbeille est vide' : 'Trash is empty')
                : (language === 'fr' ? 'Aucune vente trouvée' : 'No sales found')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{language === 'fr' ? 'Ticket' : 'Ticket'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Date' : 'Date'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Agent' : 'Agent'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Client' : 'Client'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Produits' : 'Products'}</TableHead>
                    <TableHead className="text-right">{language === 'fr' ? 'Total' : 'Total'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map(sale => (
                    <TableRow key={sale.id}>
                      <TableCell className="font-mono font-medium">{sale.ticket_mediciel}</TableCell>
                      <TableCell className="text-sm">{formatDate(sale.created_at)}</TableCell>
                      <TableCell>{sale.agent_name}</TableCell>
                      <TableCell>{sale.client_name || 'N/A'}</TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {(sale.products?.length || 0)} + {(sale.supplements?.length || 0)} comp.
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {sale.total_amount?.toLocaleString('fr-FR')} FCFA
                      </TableCell>
                      <TableCell>
                        {sale.deleted_at ? (
                          <Badge variant="destructive">
                            {language === 'fr' ? 'Supprimée' : 'Deleted'}
                          </Badge>
                        ) : sale.modification_history?.length > 0 ? (
                          <Badge 
                            variant="outline" 
                            className="bg-amber-50 text-amber-700 cursor-pointer"
                            onClick={() => showHistory(sale)}
                          >
                            <History className="w-3 h-3 mr-1" />
                            {language === 'fr' ? 'Modifiée' : 'Modified'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            OK
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {viewMode === 'trash' ? (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleRestore(sale)}>
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handlePermanentDelete(sale)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleEdit(sale)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(sale)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200">
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              {language === 'fr' ? 'Modifier la vente' : 'Edit Sale'} - #{selectedSale?.ticket_mediciel}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            {/* Client & Ticket */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'fr' ? 'Nom du client' : 'Client name'}</Label>
                <Input
                  value={editClientName}
                  onChange={(e) => setEditClientName(e.target.value)}
                />
              </div>
              <div>
                <Label>{language === 'fr' ? 'N° Ticket Mediciel' : 'Mediciel Ticket #'}</Label>
                <Input
                  value={editTicket}
                  onChange={(e) => setEditTicket(e.target.value)}
                />
              </div>
            </div>

            {/* Products */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{language === 'fr' ? 'Produits' : 'Products'}</Label>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {editProducts.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    {language === 'fr' ? 'Aucun produit' : 'No products'}
                  </p>
                ) : editProducts.map((product, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm flex-1">{product.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {product.price?.toLocaleString('fr-FR')} FCFA
                      </span>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateProductQuantity(index, -1)}>
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center">{product.quantity || 1}</span>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateProductQuantity(index, 1)}>
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeProduct(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Supplements */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{language === 'fr' ? 'Compléments' : 'Supplements'}</Label>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {editSupplements.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    {language === 'fr' ? 'Aucun complément' : 'No supplements'}
                  </p>
                ) : editSupplements.map((supplement, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 p-2 rounded">
                    <span className="text-sm flex-1">{supplement.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {supplement.price?.toLocaleString('fr-FR')} FCFA
                      </span>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateSupplementQuantity(index, -1)}>
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center">{supplement.quantity || 1}</span>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateSupplementQuantity(index, 1)}>
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeSupplement(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Single Add Product Button */}
            {!showProductSearch && (
              <Button 
                variant="outline" 
                className="w-full border-dashed border-2 border-cyan-400 text-cyan-600 hover:bg-cyan-50"
                onClick={() => {
                  setShowProductSearch(true);
                  setProductSearchQuery('');
                  setProductSearchResults([]);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Ajouter un produit ou complément' : 'Add a product or supplement'}
              </Button>
            )}

            {/* Product Search Panel */}
            {showProductSearch && (
              <div className="border-2 border-dashed border-cyan-300 rounded-lg p-4 bg-cyan-50/50">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-cyan-700 font-medium">
                    {language === 'fr' ? '🔍 Rechercher un produit' : '🔍 Search product'}
                  </Label>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-6 w-6 p-0"
                    onClick={() => {
                      setShowProductSearch(false);
                      setProductSearchQuery('');
                      setProductSearchResults([]);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder={language === 'fr' ? 'Tapez le nom du produit (ex: dynamogen, doliprane...)' : 'Type product name...'}
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="pl-9"
                    autoFocus
                  />
                </div>
                
                {/* Search Results */}
                {searchingProducts && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
                    <span className="ml-2 text-sm text-gray-500">
                      {language === 'fr' ? 'Recherche...' : 'Searching...'}
                    </span>
                  </div>
                )}
                
                {!searchingProducts && productSearchResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg bg-white">
                    {productSearchResults.map((product, idx) => (
                      <div 
                        key={product.id || idx}
                        className={`flex items-center justify-between p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${product.is_supplement ? 'bg-green-50/50' : ''}`}
                        onClick={() => addProductToSale(product)}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium truncate">
                            {product.name}
                            {product.is_supplement && (
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                Complément
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">{product.code || product.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-cyan-600">
                            {product.price?.toLocaleString('fr-FR')} FCFA
                          </span>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {!searchingProducts && productSearchQuery.length >= 2 && productSearchResults.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-3">
                    {language === 'fr' ? 'Aucun produit trouvé' : 'No products found'}
                  </p>
                )}
              </div>
            )}

            {/* Total */}
            <div className="bg-cyan-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">{language === 'fr' ? 'Nouveau total' : 'New total'}</span>
                <span className="text-2xl font-bold text-cyan-700">
                  {calculateTotal().toLocaleString('fr-FR')} FCFA
                </span>
              </div>
            </div>

            {/* Reason */}
            <div>
              <Label className="text-red-600">
                * {language === 'fr' ? 'Raison de la modification' : 'Reason for modification'}
              </Label>
              <Textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder={language === 'fr' ? 'Expliquez pourquoi cette modification est nécessaire...' : 'Explain why this modification is needed...'}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="shrink-0 px-6 py-3 border-t border-zinc-200">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Enregistrer les modifications' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-h-[85vh] flex flex-col gap-0 p-0 max-w-md">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200">
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {language === 'fr' ? 'Supprimer la vente' : 'Delete Sale'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            <p>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer la vente #${saleToDelete?.ticket_mediciel} ?`
                : `Are you sure you want to delete sale #${saleToDelete?.ticket_mediciel}?`}
            </p>
            <p className="text-sm text-gray-500">
              {language === 'fr'
                ? 'La vente sera déplacée dans la corbeille et pourra être restaurée.'
                : 'The sale will be moved to trash and can be restored.'}
            </p>
            
            <div>
              <Label>{language === 'fr' ? 'Raison de la suppression (optionnel)' : 'Reason for deletion (optional)'}</Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder={language === 'fr' ? 'Indiquez la raison...' : 'Enter the reason...'}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="shrink-0 px-6 py-3 border-t border-zinc-200">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200">
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              {language === 'fr' ? 'Historique des modifications' : 'Modification History'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            {historyData.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                {language === 'fr' ? 'Aucune modification enregistrée' : 'No modifications recorded'}
              </p>
            ) : historyData.map((mod, index) => (
              <Card key={index}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium">{mod.modified_by_name}</p>
                      <p className="text-sm text-gray-500">{formatDate(mod.modified_at)}</p>
                    </div>
                  </div>
                  {mod.reason && (
                    <p className="text-sm bg-gray-50 p-2 rounded mb-2">
                      <strong>{language === 'fr' ? 'Raison:' : 'Reason:'}</strong> {mod.reason}
                    </p>
                  )}
                  <div className="text-sm space-y-1">
                    {Object.entries(mod.changes || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2">
                        <Badge variant="outline">{key}</Badge>
                        {typeof value === 'object' && value.old !== undefined ? (
                          <span>
                            <span className="text-red-500 line-through">{value.old}</span>
                            {' → '}
                            <span className="text-green-600">{value.new}</span>
                          </span>
                        ) : (
                          <span>{JSON.stringify(value)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <DialogFooter className="shrink-0 px-6 py-3 border-t border-zinc-200">
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesManagement;
